import { useForm } from "@tanstack/react-form";
import { useState, useRef } from "react";
import styled from "styled-components";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SelectInput } from "../../components/SelectInput";
import { Country } from "@KassaroAB/shared/types/enums/Country";
import { Form } from "../../components/Form";
import { InfoBox } from "../../components/InfoBox";
import { useTranslation } from "react-i18next";
import { useCreateResellerMutation, useUpdateResellerMutation } from "../../query/resellersQuery";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import type { RestReseller } from "@KassaroAB/shared/types/reseller";
import type { ResellerId } from "@KassaroAB/shared/types/ids";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const FileNameDisplay = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-top: ${spacing.x05}px;
`;

const ImagePreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  margin-top: ${spacing.x1}px;
  border-radius: 4px;
`;

interface ResellerFormProps {
  reseller?: RestReseller;
  resellerId?: ResellerId;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ResellerForm = ({ reseller, resellerId, onSuccess, onCancel }: ResellerFormProps) => {
  const { t } = useTranslation();
  const isEditMode = !!reseller && !!resellerId;
  const isAdmin = useIsAdmin();
  const { mutate: createReseller, isPending: isCreatePending, error: createError } =
    useCreateResellerMutation();
  const { mutate: updateReseller, isPending: isUpdatePending, error: updateError } =
    useUpdateResellerMutation();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(reseller?.logo || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: {
      name: reseller?.name || "",
      contactEmail: reseller?.contactEmail || "",
      contactEmailInfo: reseller?.contactEmailInfo || "",
      contactPhone: reseller?.contactPhone || "",
      contactPhoneInfo: reseller?.contactPhoneInfo || "",
      extraInfo: reseller?.extraInfo || "",
      city: reseller?.city || "",
      street: reseller?.street || "",
      postalcode: reseller?.postalcode || "",
      orgNumber: reseller?.orgNumber || "",
      country: reseller?.country || Country.SWEDEN,
      brand: reseller?.brand || "Kassaro",
    },
    onSubmit: ({ value }) => {
      const formData = new FormData();
      formData.append("name", value.name);
      formData.append("contactEmail", value.contactEmail);
      if (value.contactEmailInfo) {
        formData.append("contactEmailInfo", value.contactEmailInfo);
      }
      if (value.contactPhone) {
        formData.append("contactPhone", value.contactPhone);
      }
      if (value.contactPhoneInfo) {
        formData.append("contactPhoneInfo", value.contactPhoneInfo);
      }
      if (value.extraInfo) {
        formData.append("extraInfo", value.extraInfo);
      }
      formData.append("city", value.city);
      formData.append("street", value.street);
      formData.append("postalcode", value.postalcode);
      formData.append("orgNumber", value.orgNumber);
      formData.append("country", value.country);
      if (isAdmin) {
        formData.append("brand", value.brand || "Kassaro");
      } else {
        formData.append("brand", "Kassaro");
      }
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      if (isEditMode && resellerId) {
        updateReseller(
          { resellerId, formData },
          {
            onSuccess: () => {
              form.reset();
              setLogoFile(null);
              setLogoPreview(null);
              onSuccess();
            },
          }
        );
      } else {
        createReseller(
          formData,
          {
            onSuccess: () => {
              form.reset();
              setLogoFile(null);
              setLogoPreview(null);
              onSuccess();
            },
          }
        );
      }
    },
  });

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(reseller?.logo || null);
    }
  };

  const isLoading = isCreatePending || isUpdatePending;
  const error = createError || updateError;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {error && (
        <InfoBox variant="error">
          {error?.message ?? (isEditMode ? t("resellerForm.errorUpdating") : t("resellerForm.errorCreating"))}
        </InfoBox>
      )}

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) =>
            !value ? t("resellerForm.nameRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.resellerName")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.resellerNamePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="contactEmail"
        validators={{
          onChange: ({ value }) => {
            if (!value) return t("resellerForm.contactEmailRequired");
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return t("resellerForm.emailInvalid");
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.contactEmail")}</FormLabel>
            <Input
              type="email"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.contactEmailPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="contactEmailInfo">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.contactEmailInfo")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("resellerForm.contactEmailInfoPlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="contactPhone">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.contactPhone")}</FormLabel>
            <Input
              type="tel"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("resellerForm.contactPhonePlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="contactPhoneInfo">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.contactPhoneInfo")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("resellerForm.contactPhoneInfoPlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="extraInfo">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.extraInfo")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("resellerForm.extraInfoPlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="street"
        validators={{
          onChange: ({ value }) =>
            !value ? t("resellerForm.streetRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.street")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.streetPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="postalcode"
        validators={{
          onChange: ({ value }) =>
            !value ? t("resellerForm.postalCodeRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.postalCode")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.postalCodePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="city"
        validators={{
          onChange: ({ value }) =>
            !value ? t("resellerForm.cityRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.city")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.cityPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="orgNumber"
        validators={{
          onChange: ({ value }) =>
            !value ? t("resellerForm.orgNumberRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.orgNumber")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("resellerForm.orgNumberPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="country">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("resellerForm.country")}</FormLabel>
            <SelectInput
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value as Country)}
              onBlur={field.handleBlur}
            >
              {Object.values(Country).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
      </form.Field>

      {isAdmin && (
        <form.Field name="brand">
          {(field) => (
            <FormGroup>
              <FormLabel>{t("resellerForm.brand")}</FormLabel>
              <Input
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t("resellerForm.brandPlaceholder")}
              />
            </FormGroup>
          )}
        </form.Field>
      )}

      <FileInputContainer>
        <FormGroup>
          <FormLabel>{t("resellerForm.logo")}</FormLabel>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
          />
          {logoFile && (
            <FileNameDisplay>
              {t("resellerForm.selectedFile", {
                name: logoFile.name,
                size: (logoFile.size / 1024).toFixed(2),
              })}
            </FileNameDisplay>
          )}
          {logoPreview && (
            <ImagePreview src={logoPreview} alt={t("resellerForm.logoPreviewAlt")} />
          )}
        </FormGroup>
      </FileInputContainer>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? isEditMode
              ? t("resellerForm.updating")
              : t("resellerForm.creating")
            : isEditMode
              ? t("resellerForm.updateReseller")
              : t("resellerForm.createReseller")}
        </Button>
      </FormActions>
    </Form>
  );
};
